import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wallet, LogOut, Package, ArrowDownCircle, ArrowUpCircle, Gift, Home, ShieldCheck, ChevronLeft, ChevronRight, AlertTriangle, Download, History, X, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ComposedChart, Line, Legend } from "recharts";
import RequireAuth from "../components/RequireAuth";
import { authApi, logout, PLAN_LABELS } from "../lib/auth";
import { LOGO_URL, BUSINESS_NAME } from "../lib/site";

const TXN_META = {
  credit: { icon: ArrowDownCircle, color: "text-emerald-600", sign: "+" },
  bonus: { icon: Gift, color: "text-[#b58900]", sign: "+" },
  debit: { icon: ArrowUpCircle, color: "text-rose-600", sign: "-" },
};

function PortalContent({ user }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState("");
  const [dayPage, setDayPage] = useState(0);
  const [repStart, setRepStart] = useState("");
  const [repEnd, setRepEnd] = useState("");
  const [preview, setPreview] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const DAYS_PER_PAGE = 10;

  // Defensive: label_scanners must never land here (their portal is /label-scan).
  useEffect(() => {
    if (user?.role === "label_scanner") navigate("/label-scan", { replace: true });
  }, [user, navigate]);

  const previewStyle = (p) => {
    const size = 256, pad = 18;
    let left = p.x + pad, top = p.y + pad;
    if (typeof window !== "undefined") {
      if (left + size > window.innerWidth) left = p.x - size - pad;
      if (top + size > window.innerHeight) top = p.y - size - pad;
    }
    return { position: "fixed", left: Math.max(8, left), top: Math.max(8, top), zIndex: 1000, pointerEvents: "none" };
  };

  const downloadReport = async () => {
    try {
      const res = await authApi.get("/shipments/me/report", {
        params: { start: repStart || undefined, end: repEnd || undefined },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `my_billing_${repStart || "all"}_${repEnd || "all"}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { /* noop */ }
  };

  const grouped = useMemo(() => {
    const map = {};
    shipments.forEach((s) => {
      const src = s.order_date || s.created_at;
      const d = src ? new Date(src).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) : "unknown";
      (map[d] = map[d] || []).push(s);
    });
    return Object.entries(map)
      .map(([date, items]) => {
        const tot = {
          pending:   { n: 0, amt: 0, items: 0 },
          shipped:   { n: 0, amt: 0, items: 0 },
          cancelled: { n: 0, amt: 0, items: 0 },
        };
        items.forEach((s) => {
          const st = s.status === "billed" ? "shipped" : s.status; // billed rolls into shipped for the daily tally
          if (tot[st]) {
            tot[st].n += 1;
            tot[st].amt += Number(s.sale_amount || 0);
            tot[st].items += Object.values(s.sizes || {}).reduce((a, b) => a + (Number(b) || 0), 0);
          }
        });
        return { date, items, totals: tot };
      })
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [shipments]);

  const totalPages = Math.max(1, Math.ceil(grouped.length / DAYS_PER_PAGE));
  const visibleDays = selectedDay
    ? grouped.filter((g) => g.date === selectedDay)
    : grouped.slice(dayPage * DAYS_PER_PAGE, dayPage * DAYS_PER_PAGE + DAYS_PER_PAGE);

  // 7-day trend: last 7 IST dates (oldest → newest) with total sale + charge + order count per day.
  // Sales are attributed to the day the order came in (order_date) and only count
  // orders that have actually been processed (status shipped/billed). Pending, cancelled
  // and archived orders are excluded so the chart reflects real, realised sales.
  const trend = useMemo(() => {
    const buckets = {};
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i);
      const key = d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
      buckets[key] = { date: key, orders: 0, charge: 0, sales: 0 };
    }
    shipments.forEach((s) => {
      const src = s.order_date || s.created_at;
      if (!src) return;
      const key = new Date(src).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
      if (!buckets[key]) return;
      const processed = s.status === "shipped" || s.status === "billed";
      if (!processed) return;
      buckets[key].orders += 1;
      buckets[key].charge += Number(s.charge || 0);
      buckets[key].sales += Number(s.sale_amount || 0);
    });
    const rows = Object.values(buckets);
    const totalCharge = Math.round(rows.reduce((a, b) => a + b.charge, 0));
    const totalSales = Math.round(rows.reduce((a, b) => a + b.sales, 0));
    const totalOrders = rows.reduce((a, b) => a + b.orders, 0);
    return {
      rows: rows.map((r) => ({
        ...r,
        sales: Math.round(r.sales),
        charge: Math.round(r.charge),
        label: new Date(r.date).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: "short" }),
      })),
      weekTotal: totalCharge,
      weekSales: totalSales,
      weekOrders: totalOrders,
      dailyAvg: Math.round(totalCharge / 7),
      salesAvg: Math.round(totalSales / 7),
    };
  }, [shipments]);
  const recommendedRecharge = Math.max(500, Math.ceil((trend.weekTotal * 1.5) / 100) * 100);

  const fmtDay = (d) => {
    if (d === "unknown") return "Undated";
    try { return new Date(d).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: "short", day: "numeric", month: "short", year: "numeric" }); }
    catch { return d; }
  };
  const STATUS_BADGE = (st) => st === "billed" ? "bg-emerald-100 text-emerald-700" : st === "shipped" ? "bg-sky-100 text-sky-700" : st === "cancelled" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700";

  useEffect(() => {
    authApi
      .get("/wallet/me")
      .then((res) => setData(res.data))
      .catch(() => setData({ balance: 0, plan: "none", transactions: [], orders: [] }))
      .finally(() => setLoading(false));
    authApi.get("/shipments/me").then((res) => setShipments(res.data)).catch(() => setShipments([]));
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt={BUSINESS_NAME} className="w-9 h-9 rounded-full border border-slate-700" />
            <span className="font-extrabold uppercase tracking-tight text-sm hidden sm:block">{BUSINESS_NAME}</span>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={() => setHistoryOpen(true)} data-testid="wallet-history-btn" title="Wallet history & past charges" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide bg-slate-800 hover:bg-slate-700 px-3 py-2">
              <History className="w-4 h-4" /> <span className="hidden sm:inline">History</span>
            </button>
            {user?.role === "admin" && (
              <Link to="/admin-pod" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide bg-[#FFD400] text-slate-950 px-3 py-2" data-testid="goto-admin">
                <ShieldCheck className="w-4 h-4" /> Admin
              </Link>
            )}
            <Link to="/" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide hover:text-[#FFD400]">
              <Home className="w-4 h-4" /> <span className="hidden sm:inline">Home</span>
            </Link>
            <button onClick={handleLogout} data-testid="logout-btn" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide bg-slate-800 hover:bg-slate-700 px-3 py-2">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8">
        {/* Greeting */}
        <div className="flex items-center gap-4">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-14 h-14 rounded-full border-2 border-slate-950" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-slate-950 text-white flex items-center justify-center font-extrabold text-xl">{(user?.name || "B")[0]}</div>
          )}
          <div>
            <h1 className="text-2xl font-extrabold uppercase tracking-tight leading-none">Welcome, {data?.brand_name || user?.name?.split(" ")[0] || "Brand"}</h1>
            <p className="text-sm text-slate-500 font-medium">{user?.email}</p>
          </div>
        </div>

        {/* Recharge warning */}
        {data?.recharge_needed && (
          <div data-testid="recharge-warning" className="flex items-start gap-3 border-2 border-rose-500 bg-rose-50 text-rose-800 px-4 py-3">
            <AlertTriangle className="w-6 h-6 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-extrabold uppercase">Please recharge your wallet</p>
              <p>Your unprocessed orders total <b>₹{data.pending_liability}</b>, which exceeds your wallet balance of <b>₹{data.balance}</b>. Top up to keep your orders processing without interruption.</p>
            </div>
          </div>
        )}

        {/* Wallet / COD summary + 7-day sales trend */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* LEFT: Chart card (2 cols) */}
          <div className="lg:col-span-2 bg-white border-2 border-slate-950 p-5 shadow-[6px_6px_0px_0px_rgba(255,212,0,0.8)]" data-testid="trend-card">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Last 7 days · Realised sales &amp; shipping (processed orders)</p>
                <div className="flex flex-wrap items-baseline gap-4 mt-1">
                  <div>
                    <p className="text-3xl sm:text-4xl font-extrabold font-mono text-emerald-700" data-testid="week-sales">₹{trend.weekSales.toLocaleString("en-IN")}</p>
                    <p className="text-[10px] font-mono text-slate-500 uppercase">Sales · avg ₹{trend.salesAvg.toLocaleString("en-IN")}/day</p>
                  </div>
                  <div>
                    <p className="text-xl sm:text-2xl font-extrabold font-mono text-slate-950" data-testid="week-total">₹{trend.weekTotal.toLocaleString("en-IN")}</p>
                    <p className="text-[10px] font-mono text-slate-500 uppercase">Shipping · avg ₹{trend.dailyAvg}/day · {trend.weekOrders} order{trend.weekOrders === 1 ? "" : "s"}</p>
                  </div>
                </div>
              </div>
              {data?.shipping_mode !== "self" && (
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Recommended top-up</p>
                  <p className="text-lg font-extrabold font-mono text-emerald-700" data-testid="recommended-recharge">₹{recommendedRecharge}</p>
                  <p className="text-[10px] font-mono text-slate-400">~1.5× last week's shipping</p>
                </div>
              )}
            </div>
            <div className="h-44 sm:h-48" data-testid="trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend.rows} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fontWeight: 700, fill: "#64748b", textTransform: "uppercase" }} />
                  <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(v) => v >= 1000 ? `₹${Math.round(v/1000)}k` : `₹${v}`} width={50} />
                  <Tooltip
                    cursor={{ fill: "rgba(255,212,0,0.15)" }}
                    contentStyle={{ background: "#0f172a", border: "2px solid #0f172a", color: "#fff", fontSize: 12, fontFamily: "monospace" }}
                    labelFormatter={(l, arr) => arr && arr[0] ? new Date(arr[0].payload.date).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: "long", day: "numeric", month: "short" }) : l}
                    formatter={(v, n, p) => {
                      if (n === "sales") return [`₹${Number(v).toLocaleString("en-IN")} · ${p.payload.orders} order${p.payload.orders === 1 ? "" : "s"}`, "Sales"];
                      if (n === "charge") return [`₹${Number(v).toLocaleString("en-IN")}`, "Shipping"];
                      return [v, n];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase" }} iconType="square" />
                  <Bar dataKey="sales" name="Sales" fill="#059669" radius={[3, 3, 0, 0]} maxBarSize={44} />
                  <Line dataKey="charge" name="Shipping" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 3, fill: "#0f172a" }} activeDot={{ r: 5 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* RIGHT: Compact wallet / billing summary */}
          <div className="bg-slate-950 text-white border-2 border-slate-950 p-6 flex flex-col justify-between">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
                <Wallet className="w-3.5 h-3.5 text-[#FFD400]" /> {data?.shipping_mode === "self" ? "Weekly Payable (COD)" : "Wallet Balance"}
              </p>
              <p className="text-4xl font-extrabold font-mono mt-2" data-testid={data?.shipping_mode === "self" ? "weekly-payable" : "wallet-balance"}>
                ₹{loading ? "…" : (data?.shipping_mode === "self" ? (data?.weekly_payable ?? 0) : (data?.balance ?? 0))}
              </p>
              <p className="text-[10px] font-mono text-slate-400 mt-1.5 uppercase">
                {data?.shipping_mode === "self" ? "Settled weekly against COD remittance" : "Charged per order on ship"}
              </p>
            </div>
            <div className="mt-4 pt-4 border-t border-slate-800">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{data?.shipping_mode === "self" ? "Billing" : "Current Plan"}</p>
              <p className="text-lg font-extrabold uppercase mt-1" data-testid="wallet-plan">{data?.shipping_mode === "self" ? "COD / Weekly" : PLAN_LABELS[data?.plan || "none"]}</p>
              {data?.shipping_mode !== "self" && data?.plan === "scale" && <p className="text-[10px] text-slate-400 mt-1 font-medium">5% off · free processing · priority</p>}
              {data?.shipping_mode !== "self" && data?.plan === "growth" && <p className="text-[10px] text-slate-400 mt-1 font-medium">5% off · faster processing</p>}
            </div>
          </div>
        </div>

        {/* Shipments */}
        <section>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-extrabold uppercase tracking-tight flex items-center gap-2"><Package className="w-5 h-5" /> Daily Orders</h2>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block">Report from</label>
                <input type="date" value={repStart} onChange={(e) => setRepStart(e.target.value)} data-testid="report-start" className="border-2 border-slate-950 px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-slate-500 block">to</label>
                <input type="date" value={repEnd} onChange={(e) => setRepEnd(e.target.value)} data-testid="report-end" className="border-2 border-slate-950 px-2 py-1.5 text-sm" />
              </div>
              <button onClick={downloadReport} data-testid="report-download" className="flex items-center gap-1.5 bg-slate-950 text-white text-xs font-bold uppercase px-4 py-2.5 border border-slate-950 hover:bg-slate-800"><Download className="w-4 h-4" /> Excel</button>
              {grouped.length > 0 && (
                <select
                  value={selectedDay}
                  onChange={(e) => { setSelectedDay(e.target.value); setDayPage(0); }}
                  data-testid="day-filter"
                  className="border-2 border-slate-950 px-3 py-2 text-sm font-semibold bg-white"
                >
                  <option value="">All days</option>
                  {grouped.map((g) => (
                    <option key={g.date} value={g.date}>{fmtDay(g.date)} ({g.items.length})</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="space-y-6" data-testid="shipments-list">
            {grouped.length === 0 ? (
              <div className="bg-white border-2 border-slate-950 p-6"><p className="text-sm text-slate-500 font-medium">No orders yet.</p></div>
            ) : (
              visibleDays.map((g) => (
                <div key={g.date} className="bg-white border-2 border-slate-950" data-testid={`day-group-${g.date}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-950 text-white px-4 py-2.5">
                    <span className="font-extrabold uppercase tracking-wide text-sm">{fmtDay(g.date)}</span>
                    <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest" data-testid={`day-totals-${g.date}`}>
                      <span className="bg-amber-400 text-slate-950 px-2 py-1" data-testid={`day-total-pending-${g.date}`}>Pending {g.totals.pending.n} · {g.totals.pending.items} pcs · ₹{g.totals.pending.amt.toLocaleString("en-IN")}</span>
                      <span className="bg-emerald-500 text-white px-2 py-1" data-testid={`day-total-shipped-${g.date}`}>Shipped {g.totals.shipped.n} · {g.totals.shipped.items} pcs · ₹{g.totals.shipped.amt.toLocaleString("en-IN")}</span>
                      <span className="bg-rose-500 text-white px-2 py-1" data-testid={`day-total-cancelled-${g.date}`}>Cancelled {g.totals.cancelled.n} · {g.totals.cancelled.items} pcs · ₹{g.totals.cancelled.amt.toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                  <div className="divide-y divide-slate-200">
                    {g.items.map((s) => (
                      <div key={s.shipment_id} className="p-4 flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <div className="flex flex-wrap gap-1.5">
                            {(s.products && s.products.length > 0) ? (
                              s.products.map((p, i) => (
                                p.image
                                  ? <img
                                      key={i} src={p.image} alt={p.title || ""} title={p.title}
                                      className="w-12 h-12 object-cover border border-slate-300 cursor-zoom-in"
                                      onMouseEnter={(e) => setPreview({ src: p.image, x: e.clientX, y: e.clientY })}
                                      onMouseMove={(e) => setPreview((pr) => pr ? { ...pr, x: e.clientX, y: e.clientY } : pr)}
                                      onMouseLeave={() => setPreview(null)}
                                    />
                                  : <div key={i} className="w-12 h-12 bg-slate-100 border border-slate-200 flex items-center justify-center"><Package className="w-5 h-5 text-slate-300" /></div>
                              ))
                            ) : (
                              <div className="w-12 h-12 bg-slate-100 border border-slate-200 flex items-center justify-center"><Package className="w-5 h-5 text-slate-300" /></div>
                            )}
                          </div>
                          <div>
                            <p className="font-bold text-slate-900">{s.order_name}</p>
                            {s.products && s.products.length > 0 && (
                              <p className="text-[11px] text-slate-600 leading-tight max-w-[260px] truncate" title={s.products.map((p) => p.title).join(", ")}>
                                {s.products.map((p) => p.title).join(", ")}
                              </p>
                            )}
                            <p className="text-xs text-slate-500 font-mono">{Object.entries(s.sizes || {}).map(([k, v]) => `${v}×${k}`).join(", ")}</p>
                            <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                              {s.expected_ship_date ? `Dispatch: ${s.expected_ship_date} (${s.same_day ? "Same-day" : "Next-day"})` : "Awaiting dispatch"}
                              {s.tracking_number ? ` · Tracking: ${s.tracking_number}` : ""}
                            </p>
                          </div>
                        </div>
                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 ${STATUS_BADGE(s.status)}`}>{s.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          {!selectedDay && totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-5" data-testid="day-pagination">
              <button
                onClick={() => setDayPage((p) => Math.max(0, p - 1))}
                disabled={dayPage === 0}
                data-testid="day-prev"
                className="flex items-center gap-1 border-2 border-slate-950 px-4 py-2 text-sm font-bold uppercase disabled:opacity-30 hover:bg-slate-100"
              ><ChevronLeft className="w-4 h-4" /> Prev</button>
              <span className="text-sm font-mono font-bold">Page {dayPage + 1} / {totalPages}</span>
              <button
                onClick={() => setDayPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={dayPage >= totalPages - 1}
                data-testid="day-next"
                className="flex items-center gap-1 border-2 border-slate-950 px-4 py-2 text-sm font-bold uppercase disabled:opacity-30 hover:bg-slate-100"
              >Next <ChevronRight className="w-4 h-4" /></button>
            </div>
          )}
        </section>

        {/* Transaction / order history is now inside the History modal opened from the top bar. */}
      </main>

      {/* Wallet History modal */}
      {historyOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="wallet-history-modal" onClick={() => setHistoryOpen(false)}>
          <div className="bg-slate-50 border-2 border-slate-950 w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 px-5 py-4 bg-slate-950 text-white">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#FFD400]">Wallet History</p>
                <p className="text-sm font-extrabold uppercase truncate">Past charges &amp; transactions</p>
              </div>
              <button onClick={() => setHistoryOpen(false)} data-testid="wallet-history-close" className="bg-white text-slate-950 hover:bg-[#FFD400] p-2 border-2 border-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-5">
              <section>
                <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Wallet transactions</h3>
                <div className="bg-white border-2 border-slate-950" data-testid="txn-list">
                  {(!data?.transactions || data.transactions.length === 0) ? (
                    <p className="p-4 text-sm text-slate-500 font-medium">No transactions yet.</p>
                  ) : (
                    <div className="divide-y divide-slate-200 max-h-72 overflow-y-auto">
                      {data.transactions.map((t) => {
                        const meta = TXN_META[t.type] || TXN_META.debit;
                        const Icon = meta.icon;
                        return (
                          <div key={t.txn_id} className="p-3 flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                              <Icon className={`w-5 h-5 ${meta.color}`} />
                              <div>
                                <p className="text-sm font-semibold text-slate-800">{t.note}</p>
                                <p className="text-[11px] text-slate-400 font-mono">{new Date(t.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-extrabold font-mono ${meta.color}`}>{meta.sign}₹{t.amount}</p>
                              <p className="text-[11px] text-slate-400 font-mono">Bal ₹{t.balance_after}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>

              {data?.orders && data.orders.length > 0 && (
                <section>
                  <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">POD orders</h3>
                  <div className="bg-white border-2 border-slate-950" data-testid="orders-list">
                    <div className="divide-y divide-slate-200 max-h-72 overflow-y-auto">
                      {data.orders.map((o) => (
                        <div key={o.order_id} className="p-3 flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="font-bold text-slate-900 text-sm">{o.quantity} × {o.fabric}</p>
                            <p className="text-[11px] text-slate-500 font-mono">{o.print_type_label}</p>
                            <p className="text-[10px] text-slate-400 font-mono mt-0.5">Placed: {o.created_at ? new Date(o.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}{o.status === "shipped" && o.shipped_at ? ` · Shipped: ${new Date(o.shipped_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}` : ""}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-extrabold font-mono text-sm">₹{o.total}</p>
                            <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 ${o.status === "shipped" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{o.status}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
      )}
      {preview && (
        <img
          src={preview.src}
          alt=""
          style={previewStyle(preview)}
          className="w-64 h-64 object-cover border-2 border-slate-950 shadow-2xl bg-white"
        />
      )}
    </div>
  );
}

export default function PodPortal() {
  const [impersonating, setImpersonating] = useState(() => {
    try { return !!(typeof window !== "undefined" && window.sessionStorage?.getItem("imp_token")); } catch { return false; }
  });
  // Impersonation bootstrap — if the URL carries `?imp=<token>`, park it in
  // sessionStorage (per-tab), strip it from the URL and flip the banner on
  // via state so React re-renders on the very first load.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const imp = p.get("imp");
      if (imp) {
        window.sessionStorage.setItem("imp_token", imp);
        p.delete("imp");
        const q = p.toString();
        window.history.replaceState({}, "", window.location.pathname + (q ? `?${q}` : ""));
        setImpersonating(true);
      }
    } catch { /* noop */ }
  }, []);
  const exitImpersonation = () => {
    try { window.sessionStorage.removeItem("imp_token"); } catch {}
    setImpersonating(false);
    window.close();
    setTimeout(() => { window.location.href = "/admin-shipments"; }, 200);
  };
  return (
    <RequireAuth redirectTarget="/pod-portal">
      {(user) => (
        <>
          {impersonating && (
            <div className="bg-amber-400 text-slate-950 border-b-2 border-slate-950 text-xs font-bold uppercase tracking-widest px-4 py-2 flex items-center justify-between gap-3" data-testid="impersonation-banner">
              <span>Viewing as <b>{user.brand_name || user.email}</b> · read-only preview</span>
              <button onClick={exitImpersonation} data-testid="impersonation-exit" className="bg-slate-950 text-white hover:bg-slate-800 px-3 py-1">Exit</button>
            </div>
          )}
          <PortalContent user={user} />
        </>
      )}
    </RequireAuth>
  );
}
